// eslint-disable-next-line import/order
import { v4 as uuidv4 } from 'uuid';

import JWTDecode from 'jwt-decode';
import Cookies from 'universal-cookie';

import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import type { Account } from '@swf/core/types/Account';
import CookieNames from '@swf/core/types/Cookies';

export interface Session {
  timestamp: number;
  sessionId: string;
  sessionSignature: string;
  installId: string;
}

export enum LoginSource {
  Login = 'login',
  Registration = 'registration',
  VerifySession = 'verifySession'
}

export interface AuthenticatedSession {
  userWebId: string;
  signature: string;
  token: string;
  loginSource: LoginSource;
}

export interface SessionSummary {
  lastSession: boolean;
  sessionEndTime: number;
  sessionStartTime: number;
  totalPlayed: number;
  totalWon: number;
}

export interface SessionState extends Partial<Session & AuthenticatedSession> {
  serverAuth?: boolean;
  authSource: 'client' | 'server';
}

export type SessionResponse = Omit<SessionState, 'serverAuth' | 'authSource'> & {
  account?: Account;
};

interface SessionModel extends ModelConfig<SessionState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const cookies = new Cookies();
let pending = false;

const reducers = {
  setServerAuth: (state: SessionState, payload: { serverAuth: boolean }): SessionState => ({
    ...state,
    authSource: 'server',
    serverAuth: payload.serverAuth
  }),
  response: (state: SessionState, { account, ...payload }: SessionResponse): SessionState => ({
    authSource: 'client',
    ...payload
  }),
  clear: ({ authSource }: SessionState): SessionState => ({ authSource })
};

const effects = {
  async fetch(payload: undefined, { rootState, dispatch }: EffectParams) {
    if (pending) {
      return;
    }

    try {
      const domain = select.location.getCookieDomain(rootState);
      // Generate installId if not present
      let installId = cookies.get(CookieNames.INSTALL_ID);
      if (!installId) {
        installId = uuidv4()
          .split('-')
          .map((str) => parseInt(str, 36))
          .map((num) => num.toString())
          .join('')
          .substr(0, 18)
          .toUpperCase();

        cookies.set(CookieNames.INSTALL_ID, installId, { domain, path: '/' });
      }
      dispatch.session.response({
        installId,
        timestamp: Date.now()
      });
    } catch (err) {
      dispatch.session.clear();
    } finally {
      // eslint-disable-next-line require-atomic-updates
      pending = false;
    }
  }
};

const selectors = (slice: Slicer<SessionState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getJWTValues() {
    return createSelector(slice, ({ token }) => {
      try {
        return JWTDecode(token as string) as { token: string; webId: string };
      } catch (err) {
        return;
      }
    });
  },
  getLegacySession() {
    return createSelector(
      slice,
      (rootState: RootState) => select.session.getJWTValues(rootState),
      ({ token, signature, userWebId }, JWTValues) =>
        JWTValues
          ? {
              token: JWTValues.token,
              signature: cookies.get(CookieNames.SIGNATURE),
              userWebId: JWTValues.webId
            }
          : { token, signature, userWebId }
    );
  },
  getWebId() {
    return createSelector(
      (rootState: RootState) => select.session.getLegacySession(rootState),
      ({ userWebId }) => userWebId
    );
  },
  hasClientSession() {
    return slice(({ authSource }) => authSource === 'client');
  },
  /**
   * Determines if the client has a validated session token.
   * Will always be false for SSR and for first client render
   */
  isClientAuthenticated() {
    return createSelector(
      (rootState: RootState) => select.session.getLegacySession(rootState),
      ({ token }) => !!token
    );
  },
  /**
   * Determines if the user has a session token, which may not be validated.
   * May be true for SSR and first client render
   * Use "isClientAuthenticated" if you need to know the session has been validated.
   */
  isAuthenticated() {
    return createSelector(
      slice,
      (rootState: RootState) => select.session.isClientAuthenticated(rootState),
      ({ serverAuth }, clientAuth) => serverAuth || clientAuth
    );
  },
  isAuthenticationCheckComplete() {
    return createSelector(
      slice,
      (rootState: RootState) => select.session.isClientAuthenticated(rootState),
      ({ serverAuth }, clientAuth) => serverAuth === clientAuth
    );
  },
  isFirstAuthenticatedSession() {
    return slice(({ loginSource }) => loginSource === LoginSource.Registration);
  },
  isServerAuthenticated() {
    return slice(({ serverAuth }) => !!serverAuth);
  }
});

const session: SessionModel = {
  state: { authSource: 'server' },
  reducers,
  effects,
  selectors
};

export default session;
